import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import ClientPlanSkeleton from 'components/shared/skeletons/ClientPlanSkeleton';
import { dateFormat } from 'common/constants/date';
import StyledOverviewProfileDivider from 'common/theme/overview-profile/StyledOverviewProfileDivider';
import StyledOverviewProfileSubtitle from 'common/theme/overview-profile/StyledOverviewProfileSubtitle';
import planGridSx from 'common/theme/overview-profile/planGridSx';
import { formatCurrency } from 'common/utils/format';

const tileSx = {
  minWidth: 0,
  backgroundColor: 'surface.raised',
  border: '1px solid',
  borderColor: 'hairline.main',
  borderRadius: '12px',
  padding: 1.5
};

function PlanDetailTile({ label, value }) {
  return (
    <Box sx={tileSx}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 700,
          fontSize: '0.62rem'
        }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, marginTop: 0.25, overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
    </Box>
  );
}

PlanDetailTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired
};

function ClientPlan({ client = null, loading }) {
  const { t, i18n } = useTranslation(['labels']);
  const dateLocale = i18n.language === 'es' ? es : enUS;
  const dueDate = useMemo(
    () =>
      client?.last_client_payment?.end_date
        ? format(parseISO(client.last_client_payment.end_date), dateFormat, {
            locale: dateLocale
          })
        : '',
    [client]
  );
  const planPrice = useMemo(
    () => (client?.plan_id?.price ? formatCurrency(client.plan_id.price) : ''),
    [client]
  );
  // Fecha de fin EFECTIVA de Active Plus (puede venir extendida por días sin clases).
  const activePlusEnd = useMemo(
    () =>
      client?.active_plus_end_date
        ? format(parseISO(client.active_plus_end_date), dateFormat, { locale: dateLocale })
        : '',
    [client, dateLocale]
  );

  const noValue = t('notAvailable', { ns: 'labels', defaultValue: 'Sin dato' });
  const details = [
    { key: 'name', label: t('name', { ns: 'labels' }), value: client?.plan_id?.name || noValue },
    { key: 'price', label: t('price', { ns: 'labels' }), value: planPrice || noValue },
    {
      key: 'sessionsPaid',
      label: t('sessionsPaid', { ns: 'labels' }),
      value: client?.plan_id?.sessions ?? noValue
    },
    {
      key: 'sessionsAvailable',
      label: t('sessionsAvailable', { ns: 'labels' }),
      value: client?.available_sessions ?? noValue
    },
    { key: 'dueDate', label: t('dueDate', { ns: 'labels' }), value: dueDate || noValue }
  ];

  return (
    <>
      <StyledOverviewProfileSubtitle component="h4">
        {t('planDetails', { ns: 'labels' })}
      </StyledOverviewProfileSubtitle>
      <StyledOverviewProfileDivider />
      {loading ? (
        <ClientPlanSkeleton />
      ) : (
        <>
          {/* Active Plus es ADITIVO al plan regular: se muestra como distintivo aparte. */}
          {client?.is_active_plus && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 2 }}>
              <Chip
                label="Active Plus"
                size="small"
                sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700 }}
              />
              {activePlusEnd && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('dueDate', { ns: 'labels' })}: {activePlusEnd}
                </Typography>
              )}
            </Stack>
          )}
          <Box sx={planGridSx}>
            {details.map((detail) => (
              <PlanDetailTile key={detail.key} label={detail.label} value={detail.value} />
            ))}
          </Box>
        </>
      )}
    </>
  );
}

ClientPlan.propTypes = {
  client: PropTypes.object,
  loading: PropTypes.bool.isRequired
};

export default ClientPlan;
